import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from './Checkbox';

export interface DataTableColumn<Row> {
  /** Field name on the row object; also the React key. */
  key: string;
  /** Uppercase column head. */
  label?: ReactNode;
  /** `left`/`right` are logical: start and end of the reading direction. */
  align?: 'left' | 'right' | 'center';
  /** Any CSS width, normally a token. */
  width?: CSSProperties['inlineSize'];
  /** Mono + tabular: every number, ID and code. */
  mono?: boolean;
  muted?: boolean;
  /** Allow wrapping (default nowrap). */
  wrap?: boolean;
  /** Full custom cell: badges, actions, two-line cells. */
  render?: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  /** Row identity. Default reads `row.id`. */
  rowKey?: (row: Row) => string;
  selectable?: boolean;
  selected?: string[];
  onSelectedChange?: (keys: string[]) => void;
  /** Whole-row activation (detail inspectors). Enter / Space work too. */
  onRowClick?: (row: Row) => void;
  /** The row whose detail is open, kept highlighted. */
  activeRowKey?: string | null;
  emptyMessage?: ReactNode;
  /** Caps the height (--fs-table-max-height) and scrolls under the sticky header: long rankings on a dashboard. */
  scroll?: boolean;
  style?: CSSProperties;
}

const alignClass = (align: DataTableColumn<unknown>['align']) =>
  align === 'right' ? 'fs-table__cell--end' : align === 'center' ? 'fs-table__cell--center' : undefined;

const defaultKey = (row: unknown) => String((row as { id?: unknown }).id);

const cellClass = (column: DataTableColumn<never>) =>
  [
    column.mono && 'fs-table__cell--mono',
    column.muted && 'fs-table__cell--muted',
    column.wrap && 'fs-table__cell--wrap',
    alignClass(column.align),
  ]
    .filter(Boolean)
    .join(' ') || undefined;

/** The product's one table pattern: sticky uppercase heads, hairline rules, optional selection. */
export function DataTable<Row>({
  columns,
  rows,
  rowKey = defaultKey,
  selectable = false,
  selected = [],
  onSelectedChange,
  onRowClick,
  activeRowKey,
  emptyMessage,
  scroll = false,
  style,
}: DataTableProps<Row>) {
  const { t } = useTranslation();
  const keys = rows.map(rowKey);
  const allOn = rows.length > 0 && keys.every((k) => selected.includes(k));
  const someOn = !allOn && keys.some((k) => selected.includes(k));
  const columnCount = columns.length + (selectable ? 1 : 0);
  const toggle = (key: string) => onSelectedChange?.(selected.includes(key) ? selected.filter((x) => x !== key) : [...selected, key]);
  const onRowKey = (row: Row) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <div className={scroll ? 'fs-table-wrap fs-table-wrap--scroll' : 'fs-table-wrap'} style={style}>
      <table className="fs-table">
        <thead>
          <tr>
            {selectable && (
              <th className="fs-table__select">
                <Checkbox
                  checked={allOn}
                  indeterminate={someOn}
                  ariaLabel={t('common.selectAll')}
                  // Only the visible rows are added or removed: rows selected before a filter hid them stay selected.
                  onChange={() =>
                    onSelectedChange?.(
                      allOn ? selected.filter((k) => !keys.includes(k)) : [...selected, ...keys.filter((k) => !selected.includes(k))],
                    )
                  }
                />
              </th>
            )}
            {columns.map((column) => (
              <th key={column.key} scope="col" className={alignClass(column.align)} style={{ inlineSize: column.width }}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columnCount} className="fs-table__empty">
                {emptyMessage ?? t('common.noData')}
              </td>
            </tr>
          )}
          {rows.map((row, index) => {
            const key = keys[index]!;
            const rowClasses = [
              selected.includes(key) && 'fs-table__row--selected',
              activeRowKey === key && 'fs-table__row--active',
              onRowClick && 'fs-table__row--clickable',
            ].filter(Boolean);
            return (
              <tr
                key={key}
                className={rowClasses.length > 0 ? rowClasses.join(' ') : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? onRowKey(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
              >
                {selectable && (
                  <td className="fs-table__select">
                    <Checkbox checked={selected.includes(key)} ariaLabel={t('common.selectRow')} onChange={() => toggle(key)} />
                  </td>
                )}
                {columns.map((column) => {
                  const value = column.render ? column.render(row) : ((row as Record<string, ReactNode>)[column.key] ?? '—');
                  return (
                    <td key={column.key} className={cellClass(column)} style={{ inlineSize: column.width }}>
                      {column.mono && !column.render ? <bdi>{value}</bdi> : value}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
